import { Action, configureStore, ThunkAction } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { aiReportsApi } from '../services/ai-reports.service'
import { apiKeysApi } from '../services/apiKeys.service'
import { acceptanceApi } from '../services/acceptance.service'
import { activityLogApi } from '../services/activitylog.service'
import { assessmentsApi } from '../services/assessments.service'
import { authApi } from '../services/auth.service'
import { casesApi } from '../services/cases.service'
import { companyApi } from '../services/company.service'
import { dashboardApi } from '../services/dashboard.service'
import { dashboardRiskHRApi } from '../services/dashboardRiskHR.service'
import { emoApi } from '../services/emo.service'
import { evaluationsApi } from '../services/evaluations.service'
import { exportApi } from '../services/export.service'
import { extendedCompanyApi } from '../services/extendedCompany.service'
import { filtersApi } from '../services/filters.service'
import { legalDocsApi } from '../services/legalDocs.service'
import { mailerApi } from '../services/mailer.service'
import { notificationsApi } from '../services/notifications.service'
import { onboardingApi } from '../services/onboarding.service'
import { permissionsApi } from '../services/permissions.service'
import { purchasesApi } from '../services/purchases.service'
import { registerApi } from '../services/register.service'
import { reportsApi } from '../services/reports.service'
import { statusApi } from '../services/status.service'
import { rolesApi } from '../services/roles.service'
import { settingsApi } from '../services/settings.service'
import { supportTicketApi } from '../services/supportTicket.service'
import { userApi } from '../services/user.service'
import { userActionsApi } from '../services/useractions.service'
import { usersApi } from '../services/users.service'
import { forbiddenMiddleware } from './middleWares/forbiddenMiddleware'
import { unauthenticatedMiddleware } from './middleWares/unauthenticatedMiddleware'
import { tableFilterSessionMiddleware } from './reducers/table-filter-session'
import { gettingStartedApi } from '../services/getting-started.service'
import assessmentsReducer from './reducers/assessmentsSlice'
import authReducer from './reducers/authSlice'
import caseReducer from './reducers/caseSlice'
import currencyReducer from './reducers/currencySlice'
import evaluationsReducer from './reducers/evaluationsSlice'
import extendedCompanyReducer from './reducers/extendedCompanySlice'
import filtersReducer from './reducers/filtersSlice'
import generalReducer from './reducers/generalSlice'
import legalDocsReducer from './reducers/legalDocsSlice'
import settingsReducer from './reducers/settingsSlice'
import gettingStartedReducer from './reducers/gettingStartedSlice'

// @lat: [[state#Store and RTK Query]]
export const store = configureStore({
  reducer: {
    generalReducer,
    legalDocsReducer,
    authReducer,
    evaluationsReducer,
    assessmentsReducer,
    settingsReducer,
    extendedCompanyReducer,
    filtersReducer,
    caseReducer,
    currencyReducer,
    gettingStartedReducer,

    // Add the generated reducer as a specific top-level slice
    [authApi.reducerPath]: authApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [evaluationsApi.reducerPath]: evaluationsApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [dashboardRiskHRApi.reducerPath]: dashboardRiskHRApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [extendedCompanyApi.reducerPath]: extendedCompanyApi.reducer,
    [purchasesApi.reducerPath]: purchasesApi.reducer,
    [casesApi.reducerPath]: casesApi.reducer,
    [rolesApi.reducerPath]: rolesApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [emoApi.reducerPath]: emoApi.reducer,
    [settingsApi.reducerPath]: settingsApi.reducer,
    [mailerApi.reducerPath]: mailerApi.reducer,
    [userActionsApi.reducerPath]: userActionsApi.reducer,
    [filtersApi.reducerPath]: filtersApi.reducer,
    [assessmentsApi.reducerPath]: assessmentsApi.reducer,
    [activityLogApi.reducerPath]: activityLogApi.reducer,
    [acceptanceApi.reducerPath]: acceptanceApi.reducer,
    [registerApi.reducerPath]: registerApi.reducer,
    [supportTicketApi.reducerPath]: supportTicketApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    [gettingStartedApi.reducerPath]: gettingStartedApi.reducer,
    [legalDocsApi.reducerPath]: legalDocsApi.reducer,
    [exportApi.reducerPath]: exportApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [statusApi.reducerPath]: statusApi.reducer,
    [apiKeysApi.reducerPath]: apiKeysApi.reducer,
    [aiReportsApi.reducerPath]: aiReportsApi.reducer,
  },

  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Increase warning thresholds for serializable and immutable state checks
      // These warnings are development-only and help catch bugs, but can be noisy
      // with large state/actions. They're automatically disabled in production.
      serializableCheck: {
        warnAfter: 128, // Default is 32ms, increase to 128ms
        // Blob (file download) template endpoints are non-serializable (employees + bulk enums)
        ignoredActions: ['userActionsApi/executeQuery/fulfilled', 'settingsApi/executeQuery/fulfilled'],
        ignoredPaths: ['userActionsApi.queries', 'settingsApi.queries'],
      },
      immutableCheck: {
        warnAfter: 128, // Default is 32ms, increase to 128ms
      },
    })
      .concat(unauthenticatedMiddleware)
      .concat(tableFilterSessionMiddleware)
      .concat(authApi.middleware)
      .concat(userApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(evaluationsApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(dashboardRiskHRApi.middleware)
      .concat(companyApi.middleware)
      .concat(extendedCompanyApi.middleware)
      .concat(casesApi.middleware)
      .concat(rolesApi.middleware)
      .concat(permissionsApi.middleware)
      .concat(emoApi.middleware)
      .concat(settingsApi.middleware)
      .concat(assessmentsApi.middleware)
      .concat(mailerApi.middleware)
      .concat(filtersApi.middleware)
      .concat(usersApi.middleware)
      .concat(purchasesApi.middleware)
      .concat(userActionsApi.middleware)
      .concat(activityLogApi.middleware)
      .concat(acceptanceApi.middleware)
      .concat(registerApi.middleware)
      .concat(supportTicketApi.middleware)
      .concat(onboardingApi.middleware)
      .concat(gettingStartedApi.middleware)
      .concat(legalDocsApi.middleware)
      .concat(exportApi.middleware)
      .concat(reportsApi.middleware)
      .concat(statusApi.middleware)
      .concat(apiKeysApi.middleware)
      .concat(aiReportsApi.middleware)
      .concat(forbiddenMiddleware),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
setupListeners(store.dispatch)
